import { Fragment, type ReactNode, useMemo } from 'react'
import { Route } from 'react-router-dom'
import { CompatRoute, NavLink, Link as RouterLink, useMatch } from 'react-router-dom-v5-compat'
import AssessmentIcon from '@mui/icons-material/Assessment'
import ChatIcon from '@mui/icons-material/ChatRounded'
import CreditCardIcon from '@mui/icons-material/CreditCard'
import DescriptionIcon from '@mui/icons-material/Description'
import GroupIcon from '@mui/icons-material/Group'
import ChevronLeftIcon from '@mui/icons-material/KeyboardArrowLeft'
import ListAltIcon from '@mui/icons-material/ListAlt'
import LocalOfferIcon from '@mui/icons-material/LocalOffer'
import PersonIcon from '@mui/icons-material/Person'
import StoreIcon from '@mui/icons-material/Store'
import SwapCallsIcon from '@mui/icons-material/SwapCalls'
import Box from '@mui/material/Box'
import Drawer from '@mui/material/Drawer'
import Link from '@mui/material/Link'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Toolbar from '@mui/material/Toolbar'
import { useAppSelector } from '~/store'

type NavSection = {
    to: string
    icon: ReactNode
    label: string
    permission?: string
    root?: string
    subItems?: Array<{
        to: string
        label: string
    }>
}

const DEFAULT_NAV_SECTIONS: NavSection[] = [
    {
        permission: 'module.analytics',
        root: '/home/dashboard',
        to: '/home/dashboard/overview',
        icon: <AssessmentIcon />,
        label: gettext('Analytics'),
        subItems: [
            {
                to: '/home/dashboard/overview',
                label: gettext('Overview'),
            },
            {
                to: '/home/dashboard/reports',
                label: gettext('Reports'),
            },
        ],
    },
    {
        permission: 'module.chat',
        root: '/home/chat',
        to: '/home/chat',
        icon: <ChatIcon />,
        label: gettext('Chat'),
    },
    {
        permission: 'module.conversations',
        root: '/home/conversations',
        to: '/home/conversations',
        icon: <ChatIcon />,
        label: gettext('Conversations'),
        subItems: [
            {
                to: '/home/conversations/conversations',
                label: gettext('Conversations'),
            },
        ],
    },
    {
        permission: 'module.sales',
        root: '/home/sales',
        to: '/home/sales/process',
        icon: <DescriptionIcon />,
        label: gettext('Orders'),
        subItems: [
            {
                to: '/home/sales/process',
                label: gettext('Orders'),
            },
            {
                to: '/home/sales/returns',
                label: gettext('Returns'),
            },
            {
                to: '/home/sales/pick-pack-batches',
                label: gettext('Batches'),
            },
            {
                to: '/home/sales/exports',
                label: gettext('Exports'),
            },
            {
                to: '/home/sales/seller-fleet',
                label: gettext('Seller Fleet'),
            },
        ],
    },
    {
        permission: 'module.products',
        root: '/home/products',
        to: '/home/products/catalog',
        icon: <StoreIcon />,
        label: gettext('Products'),
        subItems: [
            {
                to: '/home/products/catalog',
                label: gettext('Catalog'),
            },
            {
                to: '/home/products/prices',
                label: gettext('Prices'),
            },
        ],
    },
    {
        permission: 'module.stock',
        root: '/home/stock',
        to: '/home/stock/manager',
        icon: <ListAltIcon />,
        label: gettext('Stock'),
        subItems: [
            {
                to: '/home/stock/manager',
                label: gettext('Stock'),
            },
            {
                to: '/home/stock/channel-stock-breakdown',
                label: gettext('By Shop'),
            },
            {
                to: '/home/stock/report',
                label: gettext('Stock Report'),
            },
            {
                to: '/home/stock/adjustment-groups',
                label: gettext('Stock Adjustments'),
            },
        ],
    },
    {
        permission: 'module.products',
        root: '/home/promotions',
        to: '/home/promotions/discounts',
        icon: <LocalOfferIcon />,
        label: gettext('Promotions'),
        subItems: [
            {
                to: '/home/promotions/discounts',
                label: gettext('Discounts'),
            },
            {
                to: '/home/promotions/gift-with-purchase',
                label: gettext('Gift with Purchase'),
            },
        ],
    },
    {
        permission: 'module.settings',
        root: '/home/integrations',
        to: '/home/integrations',
        icon: <SwapCallsIcon />,
        label: gettext('Integrations'),
    },
]

const SETTINGS_NAV_SECTIONS: NavSection[] = [
    {
        to: '/home/settings/stores',
        icon: <StoreIcon />,
        label: gettext('Stores'),
    },
    {
        to: '/home/settings/users',
        icon: <PersonIcon />,
        label: gettext('Users'),
    },
    {
        to: '/home/settings/groups',
        icon: <GroupIcon />,
        label: gettext('Groups'),
    },
]

type SidebarItemProps = {
    root?: string
    to: string
    icon: ReactNode
    label: string
}

function SidebarItem({ root, to, icon, label }: SidebarItemProps) {
    return (
        <Route
            path={root ? root : to}
            exact={!root}
            children={({ match }) => (
                <Link component={RouterLink} to={to} sx={{ textDecoration: 'none' }}>
                    <ListItemButton sx={{ pl: 3 }}>
                        <ListItemIcon
                            sx={{
                                color: match ? (theme) => theme.palette.primary.main : undefined,
                            }}
                        >
                            {icon}
                        </ListItemIcon>
                        <ListItemText
                            sx={{ pl: 0.5, fontWeight: 400 }}
                            primary={label}
                            primaryTypographyProps={{
                                variant: 'subtitle1',
                                color: match ? 'primary' : 'textPrimary',
                            }}
                        />
                    </ListItemButton>
                </Link>
            )}
        />
    )
}

type SidebarSubItemProps = {
    root?: string
    to: string
    label: string
}

function SidebarSubItem({ root, to, label }: SidebarSubItemProps) {
    return (
        <CompatRoute path={root} exact={false}>
            <NavLink to={to}>
                {({ isActive }) => (
                    <ListItemButton sx={{ pl: 3 }}>
                        <ListItemText
                            sx={{ pl: 0.5, ml: 5 }}
                            primary={label}
                            primaryTypographyProps={{
                                variant: 'subtitle2',
                                color: isActive ? 'primary' : 'initial',
                                fontWeight: 400,
                            }}
                        />
                    </ListItemButton>
                )}
            </NavLink>
        </CompatRoute>
    )
}
type SidebarProps = {
    navSections: NavSection[]
    showBackButton?: boolean
}

function Sidebar({ navSections, showBackButton = false }: SidebarProps) {
    return (
        <aside>
            <Drawer
                variant="permanent"
                anchor="left"
                sx={{
                    width: 'var(--app-navbar-width)',
                    flexShrink: 0,

                    '& .MuiDrawer-paper': {
                        width: 'var(--app-navbar-width)',
                    },
                }}
            >
                <Toolbar variant="dense" />
                <List
                    sx={{
                        backgroundColor: 'white',
                        pt: 4,
                        '& > a': {
                            textDecoration: 'none',
                            color: 'inherit',
                        },
                    }}
                >
                    {showBackButton ? (
                        <ListItemButton
                            sx={{ pl: 3 }}
                            component={RouterLink}
                            to="/home/dashboard/overview"
                        >
                            <ListItemIcon>
                                <ChevronLeftIcon />
                            </ListItemIcon>
                            <ListItemText
                                sx={{ pl: 0.5, fontWeight: 400 }}
                                primary={gettext('Go back')}
                                primaryTypographyProps={{
                                    variant: 'subtitle1',
                                    color: 'textPrimary',
                                }}
                            />
                        </ListItemButton>
                    ) : null}
                    {navSections.map((section) => (
                        <Fragment key={section.root ?? section.to}>
                            <SidebarItem
                                root={section.root}
                                to={section.to}
                                icon={section.icon}
                                label={section.label}
                            />
                            {section.subItems?.map((subItem) => (
                                <SidebarSubItem
                                    root={section.root}
                                    key={subItem.to}
                                    to={subItem.to}
                                    label={subItem.label}
                                />
                            ))}
                        </Fragment>
                    ))}
                </List>
            </Drawer>
        </aside>
    )
}

export function AppLayout({ children }: { children: ReactNode }) {
    const settingsMatch = useMatch({ path: '/home/settings/*' })

    const userPermissions = useAppSelector((state) => state.initial.user.permissions)

    const navSectionsToDisplay = useMemo(() => {
        if (settingsMatch) {
            return SETTINGS_NAV_SECTIONS
        }

        return DEFAULT_NAV_SECTIONS.filter((item) => {
            return !item.permission || userPermissions.some((p) => p.codename === item.permission)
        })
    }, [userPermissions, settingsMatch])

    return (
        <Box display="flex">
            <Sidebar navSections={navSectionsToDisplay} showBackButton={!!settingsMatch} />

            <Box
                component="main"
                sx={{
                    backgroundColor: (theme) => theme.palette.grey[100],
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    overflowX: 'hidden',
                    height: '100vh',
                }}
            >
                <Toolbar variant="dense" />
                <Box
                    sx={{
                        '--app-toolbar-height': '48px',
                        display: 'grid',
                        p: 'var(--app-padding)',
                        position: 'relative',
                    }}
                >
                    {children}
                </Box>
            </Box>
        </Box>
    )
}
